import React, { useEffect, useRef, useState } from "react"
import { useRouteMatch, useHistory } from "react-router-dom"
import styles from "./index.module.scss"
import { reportData } from "../../../utils/report-data"
import {
  ErrorPlaceholder,
  EmptyPlaceholder,
} from "../../../components/placeholder"
import LoadingPage from "@/components/loading-page"
import useQuery from "@/hooks/useQuery"
import {
  formatTimestampToTxt,
  isInViewPort
} from '@/utils/common'
import {
  formatEnergyData
} from '../utils'
import {
  getDatePreviewList
} from '@/api/energy-report'

interface DateItem {
  date: string,
  avgDrivePower: number,
  avgDriveFuel: number
  avgCircumstanceTemperature: number
}

const DEFAULT_ERROR_MSG = '服务器开小差啦'

const App = () => {
  const vin = useQuery().get('vin') || ''
  const lastDay = useQuery().get('lastDay')
  const validEndTime = useQuery().get('validEndTime')
  // 是否增程车参数
  const isSuperErev = useQuery().get('isSuperErev') === 'true'
  const history = useHistory()
  const { path } = useRouteMatch()

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingError, setIsLoadingError] = useState(false)
  const [isEmptyPage, setIsEmptyPage] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [dateListObj, setDateListObj] = useState<Record<string, DateItem[]> | any[]>({})

  const nodeRef = useRef<any>(null)
  const scrollNode = useRef<any>(null)

  useEffect(() => {
    document.title = "能耗数据"
    getDateList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (lastDay !== 'undefined') {
      const innerText = formatTimestampToTxt(Number(lastDay), 'M月D日')
      setTimeout(() => {
        scrollSpecifiedDomIntoViewPort(innerText)
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateListObj])

  /**
   * 首页带过来的某周期最后一天对应的DOM若不在可视窗口内，移动到可视窗口
   */
  const scrollSpecifiedDomIntoViewPort = (innerText: string) => {
    if (nodeRef.current) {
      scrollNode.current = findTargetDateBarFromNodeTree(nodeRef.current.childNodes, innerText)
      // console.log('scrollNode.current', innerText, scrollNode.current, scrollNode.current?.innerText)
      if (scrollNode.current && !isInViewPort(scrollNode.current)) {
        scrollNode.current?.scrollIntoView()
      }
    }
  }
  
  /**
   * 从根节点树找到包含指定日期innerText的dateBar节点，这个find逻辑依赖当前DOM结构，注意改动样式布局后需要检查
   * @param nodeTree 
   * @param innerText 
   * @returns 
   */
  const findTargetDateBarFromNodeTree = (nodeTree: any, innerText: string) => {
    const nodeArr: any[] = []
    Array.from(nodeTree)?.forEach((node: any) => {
      if (node.childNodes.length > 1) {
        Array.from(node.childNodes)?.forEach((subNode: any) => {
          nodeArr.push(subNode)
        })
      }
    })
    // 获取包含指定日期的bar集合
    const nodesIncludeTarget = nodeArr.find(node => node.innerText.indexOf(innerText) !== -1)
    // 找到指定日期对应的bar
    const targetBar = nodesIncludeTarget ? Array.from(nodesIncludeTarget?.childNodes)?.find((bar: any) => bar.innerText.indexOf(innerText) !== -1) : null

    return targetBar
  }

  const handleLoading = (loading: boolean) => {
    setIsLoading(loading)
  }

  const getDateList = () => {
    handleLoading(true)
    // getDatePreviewList({ vin })
    const params = isSuperErev ? { vin, validEndTime, isSuperErev } : { vin, validEndTime }
    getDatePreviewList(params)
    .then(res => {
      if (res.code === 200) {
        setDateListObj(formatDateListData(res.data))
      } else {
        setErrorMsg(DEFAULT_ERROR_MSG)
        setIsEmptyPage(true)
      }
    }).catch(err => {
      console.log(err)
      setErrorMsg(DEFAULT_ERROR_MSG)
      setIsLoadingError(true)
    }).finally(() => {
      handleLoading(false)
    })
  }

  /**
   * 根据月份做数据聚合与数据格式转换
   * @param dateList 
   */
  const formatDateListData = (dateList: DateItem[]) => {
    if (!dateList || dateList.length === 0) {
      return {}
    }
    
    let formatData: Record<string, DateItem[]> = {}
    dateList.forEach((item, index, arr) => {
      if (index === 0) {
        const monthTxt = +item.date.split('-')[1] + '月'
        formatData[monthTxt] = [item]
      } else {
        const curYearMonth = item.date.split('-')[0] + item.date.split('-')[1]
        const preYearMonth = arr[index - 1].date.split('-')[0] + arr[index - 1].date.split('-')[1]
        const curMonthTxt = +item.date.split('-')[1] + '月'
        if (curYearMonth === preYearMonth) {
          formatData[curMonthTxt]?.push(item)
        } else {
          formatData[curMonthTxt] = [item]
        }
      }
    })

    return formatData
  }

  const formatDate = (date: string) => {
    if (!date) return '--'
    const month = date.split('-')[1]
    const day = date.split('-')[2]
    return Number(month) + '月' + Number(day) + '日'
  }
  
  const goToSingleDayPage = (date: string) => {
    // 日期项点击埋点
    reportData({pageId: 'P11141', buttonId: 'B001', vin: vin, params: {
      option: date
    }})

    // 延时跳转，等待数据上报
    setTimeout(() => {
      history.push(`/car-service/energy_report/single-day?vin=${vin}&date=${date}&isSuperErev=${isSuperErev}`)
    }, 300)
  }

  const reloadPage = () => {
    window.location.reload()
  }

  if (isLoading)
    return (
      <LoadingPage />
    )

  if (isLoadingError)
    return (
      <ErrorPlaceholder
        message={errorMsg}
        getData={reloadPage}
      ></ErrorPlaceholder>
    )

  if (isEmptyPage)
    return (
      <EmptyPlaceholder text={errorMsg}></EmptyPlaceholder>
    )

  return (
    <div className={styles.homePage} ref={nodeRef}>
      {
        Object.keys(dateListObj)?.map(key => (
          <div className={styles.monthDataContainer} key={key}>
            <div className={styles.month}>{key}</div>
            <div className={styles.dateBarContainer}>
              {
                dateListObj[key]?.map((item: DateItem, index) => (
                  <div className={styles.dateBar} onClick={() => goToSingleDayPage(item.date)} key={index}>
                    {/* <div className={styles.date}>{formatDate(item.date)}</div>
                    <div className={styles.energy}>
                      {formatEnergyData(item.avgDrivePower)}
                      <span className={styles.unit}>kWh/100km</span>
                    </div>
                    <div className={styles.temperature}>
                      {formatEnergyData(item.avgCircumstanceTemperature)}
                      <span className={styles.unit}>℃</span>
                    </div> */}
                    <div className={styles.dataContiner}>
                      <div className={styles.dateAndTemperature}>
                        <div className={styles.date}>{formatDate(item.date)}</div>
                        <div className={styles.temperature}>{formatEnergyData(item.avgCircumstanceTemperature)}℃</div>
                      </div>
                      <div className={styles.energyContainer}>
                        <div className={styles.data}>
                          {formatEnergyData(item.avgDrivePower)}
                          <span className={styles.unit}>kWh/100km</span>
                        </div>
                      </div>
                      {
                        isSuperErev && <div className={styles.energyContainer}>
                          <div className={styles.data}>
                            {formatEnergyData(item.avgDriveFuel)}
                            <span className={styles.unit}>L/100km</span>
                          </div>
                        </div>
                      }
                    </div>
                    <div className={styles.arrowRight}></div>
                  </div>
                ))
              }
            </div>
          </div>
        ))
      }
    </div>
  )
}

export default App